import {
  type InfiniteData,
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
  useMutation,
  useMutationState,
} from '@tanstack/react-query';
import { StatusCodes } from 'http-status-codes';
import { z } from 'zod';
import { isFetchError } from '@/api/errors';
import { spaceTokenMiddleware } from '@/api/interceptors/wretch/space-token';
import { createQueryKey } from '@/api/query-keys';
import { spaceDataClient } from '@/api/wretch-client';
import { type SpaceIds } from './space';
import { spaceMonitoringService } from './space-monitoring';

/*
 * A space's collections live in its data plane, reached straight from the browser through the space's REST
 * endpoint with an ephemeral dashboard token.
 */

// The API maximum. Rows carry no per-collection config, so a page is a single request.
const COLLECTIONS_PAGE_SIZE = 100;
// The server reads a manifest and a point count per listed collection, so the table asks for few at a time.
export const COLLECTIONS_TABLE_PAGE_SIZE = 10;

// `baseUrl` is the space endpoint URL, which serves REST on the HTTPS default port.
type SpaceCollectionsParams = SpaceIds & { baseUrl: string };
export type SpaceCollectionParams = SpaceCollectionsParams & { collectionName: string };

export const collectionUrl = (baseUrl: string, collectionName: string) =>
  new URL(`collections/${encodeURIComponent(collectionName)}`, baseUrl).toString();

// The space's REST endpoint, authenticated with its dashboard token.
export const spaceClient = (client: QueryClient, params: SpaceIds) =>
  spaceDataClient.middlewares([spaceTokenMiddleware({ client, ...params })]);

// The REST bridge wraps every answer, and puts the reason of a failure in `status.error`.
type RestResponse<T> = { result: T };
const restErrorBodySchema = z.object({ status: z.object({ error: z.string() }) });

/** Unwraps an answer of the REST bridge: `.json(restResult<T>)`. */
export const restResult = <T>({ result }: RestResponse<T>) => result;

type DenseVector = { size: number; distance: string; multivector?: boolean; precision?: string };

export type SpaceCollectionConfig = {
  // One dense config is the unnamed vector; a map holds named ones.
  vectors?: DenseVector | Record<string, DenseVector>;
  sparse_vectors?: Record<string, { modifier?: string; precision?: string }>;
  // A bare type name, or the type with its parameters.
  payload_indexes?: Record<string, string | { type: string }>;
};

type SpaceCollectionInfo = {
  name: string;
  // Absent until the updater has counted the collection.
  points_count?: number | null;
  config: SpaceCollectionConfig;
};

export type SpaceCollectionSummary = Pick<SpaceCollectionInfo, 'name' | 'points_count'>;

type CollectionsPage = { collections: SpaceCollectionSummary[]; next_page_offset?: string | null };

/** A collection's dense vectors, one entry each. The unnamed vector, the only one when present, has `name: null`. */
export function listDenseVectors({ vectors }: SpaceCollectionConfig) {
  if (!vectors) {
    return [];
  }
  if (isUnnamedVector(vectors)) {
    return [{ name: null, ...vectors }];
  }
  return Object.entries(vectors).map(([name, vector]) => Object.assign({ name }, vector));
}

// A named map holds configs under its keys, so only the unnamed vector has a numeric `size`.
const isUnnamedVector = (vectors: DenseVector | Record<string, DenseVector>): vectors is DenseVector =>
  typeof vectors.size === 'number';

/** A create refused because the space already has a collection by that name. */
export const isCollectionNameTaken = (error: unknown) => isFetchError(error) && error.status === StatusCodes.CONFLICT;

/** The message the REST bridge puts in its error envelope, when the failure came with one. */
export function getSpaceRestErrorMessage(error: unknown): string | undefined {
  if (!isFetchError(error)) {
    return undefined;
  }
  return restErrorBodySchema.safeParse(error.body).data?.status.error;
}

export const spaceCollectionsKey = ({ accountId, spaceId }: SpaceIds) =>
  createQueryKey('serverless', 'space-collections', accountId, spaceId);

const collectionsListKey = (params: SpaceIds) => createQueryKey(...spaceCollectionsKey(params), 'list');

// A new, dropped or restored collection changes the space's size and collection count, read from its summary metrics.
const invalidateSpaceSummary = (client: QueryClient, params: SpaceIds) =>
  client.invalidateQueries({ queryKey: spaceMonitoringService.getSpaceSummaryMetricsQuery(params).queryKey });

/** Drops what is known of a space's collections, so the next read asks the space again. */
export const invalidateSpaceCollections = (client: QueryClient, params: SpaceIds) =>
  Promise.all([
    client.invalidateQueries({ queryKey: spaceCollectionsKey(params) }),
    invalidateSpaceSummary(client, params),
  ]);

export const spaceCollectionsService = {
  /**
   * Pages of collections in storage order, which carries no meaning. The cursor is not a snapshot, so a write
   * while paging can skip or repeat a row.
   */
  listCollectionsQuery: ({
    baseUrl,
    limit = COLLECTIONS_PAGE_SIZE,
    ...params
  }: SpaceCollectionsParams & { limit?: number }) =>
    infiniteQueryOptions({
      queryKey: [...collectionsListKey(params), baseUrl, limit],
      // An empty offset is the first page.
      initialPageParam: '',
      queryFn: async ({ client, pageParam, signal }) => {
        const url = new URL('collections', baseUrl);
        url.searchParams.set('limit', String(limit));
        if (pageParam) {
          url.searchParams.set('offset', pageParam);
        }
        return spaceClient(client, params)
          .options({ signal })
          .get(url.toString())
          .json(restResult<CollectionsPage>);
      },
      getNextPageParam: (page) => page.next_page_offset ?? undefined,
    }),

  /** One collection by its exact name, or `null` when the space has none by that name. */
  getCollectionQuery: ({ baseUrl, collectionName, ...params }: SpaceCollectionParams) =>
    queryOptions({
      queryKey: [...spaceCollectionsKey(params), 'get', baseUrl, collectionName],
      queryFn: async ({ client, signal }) => {
        try {
          return await spaceClient(client, params)
            .options({ signal })
            .get(collectionUrl(baseUrl, collectionName))
            .json(restResult<SpaceCollectionInfo>);
        } catch (error) {
          if (isFetchError(error) && error.isNotFound) {
            return null;
          }
          throw error;
        }
      },
    }),
};

// The values the REST bridge accepts when creating a collection (`api/serverless-collections.json`).
export const DISTANCES = ['Cosine', 'Euclid', 'Dot', 'Manhattan'] as const;
export const PRECISION_TIERS = ['low', 'medium', 'high', 'full'] as const;
export const PAYLOAD_INDEX_TYPES = ['keyword', 'integer', 'float', 'uuid', 'datetime', 'text', 'geo', 'bool'] as const;
export const TOKENIZERS = ['prefix', 'whitespace', 'word', 'multilingual'] as const;
type PrecisionTier = (typeof PRECISION_TIERS)[number];

type DenseVectorRequest = {
  size: number;
  distance: (typeof DISTANCES)[number];
  multivector?: boolean;
  precision?: PrecisionTier;
};

type PayloadIndexRequest =
  | (typeof PAYLOAD_INDEX_TYPES)[number]
  | { type: 'keyword'; prefix: boolean }
  | { type: 'integer'; lookup: boolean; range: boolean }
  | {
      type: 'text';
      tokenizer: (typeof TOKENIZERS)[number];
      lowercase: boolean;
      phrase_matching: boolean;
      ascii_folding?: boolean;
      min_token_len?: number;
      max_token_len?: number;
      stopwords?: { languages?: string[]; custom?: string[] };
      stemmer?: { type: 'snowball'; language: string };
    };

/** The body of `PUT /collections/{name}`. Vectors and payload indexes cannot change after creation. */
export type SpaceCollectionCreateConfig = {
  vectors?: DenseVectorRequest | Record<string, DenseVectorRequest>;
  sparse_vectors?: Record<string, { modifier?: 'idf'; precision?: PrecisionTier }>;
  payload_indexes?: Record<string, PayloadIndexRequest>;
};

/**
 * Creates a collection with its whole config in one request. A taken name answers 409 and a full quota 429, both
 * with the reason in the error envelope.
 */
export const useCreateSpaceCollectionMutation = () =>
  useMutation({
    mutationFn: (
      { baseUrl, collectionName, config, ...params }: SpaceCollectionParams & { config: SpaceCollectionCreateConfig },
      { client },
    ) => spaceClient(client, params).put(config, collectionUrl(baseUrl, collectionName)).res(),
    onSuccess: (_data, { accountId, spaceId }, _onMutateResult, { client }) =>
      Promise.all([
        client.resetQueries({ queryKey: spaceCollectionsKey({ accountId, spaceId }) }),
        invalidateSpaceSummary(client, { accountId, spaceId }),
      ]),
  });

const deleteCollectionKey = createQueryKey('serverless', 'delete-space-collection');

/**
 * Deletes a collection. The call returns once its data is purged, so it carries no timeout signal: a large
 * collection takes a while.
 */
export const useDeleteSpaceCollectionMutation = () =>
  useMutation({
    mutationKey: deleteCollectionKey,
    mutationFn: async ({ baseUrl, collectionName, ...params }: SpaceCollectionParams, { client }) => {
      try {
        await spaceClient(client, params).delete(collectionUrl(baseUrl, collectionName)).res();
      } catch (error) {
        // A repeated delete answers 404: the collection is gone either way.
        if (!isFetchError(error) || !error.isNotFound) {
          throw error;
        }
      }
    },
    // Cursors do not survive a write, so the list starts over from its first page. It keeps that page, minus the
    // deleted row, while it is asked again: a reset would blank the table into a skeleton.
    onSuccess: (_data, { accountId, spaceId, collectionName }, _onMutateResult, { client }) => {
      client.setQueriesData<InfiniteData<CollectionsPage, string>>(
        { queryKey: collectionsListKey({ accountId, spaceId }) },
        (data) =>
          data && {
            pages: data.pages.slice(0, 1).map(({ collections, next_page_offset }) => ({
              collections: collections.filter(({ name }) => name !== collectionName),
              next_page_offset,
            })),
            pageParams: data.pageParams.slice(0, 1),
          },
      );
      return invalidateSpaceCollections(client, { accountId, spaceId });
    },
  });

/**
 * Names of the space's collections with a delete still running, wherever it was started. The data plane reports
 * no deleting state, so this is only known in this tab.
 */
export const useDeletingSpaceCollections = ({ spaceId }: Pick<SpaceIds, 'spaceId'>) =>
  useMutationState({
    filters: {
      mutationKey: deleteCollectionKey,
      status: 'pending',
      predicate: (mutation) => (mutation.state.variables as SpaceCollectionParams).spaceId === spaceId,
    },
    select: (mutation) => (mutation.state.variables as SpaceCollectionParams).collectionName,
  });
